<script>
  import plotly from 'plotly';

  export let backend;

  const RESOLUTIONS = [
    {name: "5m", seconds: 5 * 60},
    {name: "30m", seconds: 30 * 60},
    {name: "1h", seconds: 60 * 60},
    {name: "6h", seconds: 6 * 60 * 60},
    {name: "12h", seconds: 12 * 60 * 60},
    {name: "24h", seconds: 24 * 60 * 60}
  ];

  // `key` is what ends up in the shared url, `name` is what the select shows
  const RANGES = [
    {key: "24h", name: "Последние 24 часа", hours: 24},
    {key: "7d", name: "Последние 7 дней", hours: 7 * 24},
    {key: "30d", name: "Последние 30 дней", hours: 30 * 24},
    {key: "3mo", name: "Последние 3 месяца", hours: 90 * 24},
    {key: "6mo", name: "Последние 6 месяцев", hours: 180 * 24},
    {key: "1y", name: "Последний год", hours: 365 * 24}
  ];

  const LAYOUT = {
    uirevision: 'true',
    xaxis: {autorange: true},
    yaxis: {autorange: true, title: 'цена'},
    yaxis2: {autorange: true, title: 'офферов', overlaying: 'y', side: 'right'},
    legend: {orientation: 'h'},
    margin: {t: 40, r: 60, b: 40, l: 60},
    plot_bgcolor: "#212529",
    paper_bgcolor: "#212529",
    font: {color: "white"},
    hovermode: 'x unified',
  };

  // every select is mirrored into the query string so a dashboard view can be shared as a link;
  // each value is honoured once, when the list it belongs to first becomes available
  const shared = new URLSearchParams(window.location.search);

  function fromUrl(list, param, fallback, key = "name") {
    let name = shared.get(param);
    shared.delete(param);
    return list.find(it => it[key] === name) || fallback;
  }

  let games = [];
  let providers = [];

  let game = null;
  let itemType = null;
  let descriptor = null;
  let provider = null;
  let resolution = fromUrl(RESOLUTIONS, "resolution", RESOLUTIONS[2]);
  let range = fromUrl(RANGES, "range", RANGES[1], "key");

  let segments = [];
  let loading = false;
  let error = null;

  let pricePlotDiv = null;
  let quantityPlotDiv = null;

  backend.games().then(g => {
    games = g;
    if (game == null) {
      game = fromUrl(g, "game", g[0]);
    }
  });
  backend.providers().then(ps => {
    providers = ps;
    if (provider == null) {
      provider = ps.find(it => it === shared.get("provider")) || ps[0];
      shared.delete("provider");
    }
  });

  // reset the dependent selects when a parent one changes, exactly like the grafana variable chain
  $: if (game != null && (itemType == null || !game.subtypes.includes(itemType))) {
    itemType = fromUrl(game.subtypes, "item", game.subtypes[0] || null);
  }
  $: if (itemType != null && (descriptor == null || !itemType.descriptors.includes(descriptor))) {
    descriptor = fromUrl(itemType.descriptors, "descriptor", itemType.descriptors[0] || null);
  }

  $: load(game, itemType, descriptor, provider, resolution, range);
  $: publish(game, itemType, descriptor, provider, resolution, range);

  function publish(game, itemType, descriptor, provider, resolution, range) {
    if (game == null || itemType == null || descriptor == null || provider == null) {
      return;
    }
    let query = new URLSearchParams({
      game: game.name,
      item: itemType.name,
      descriptor: descriptor.name,
      provider: provider,
      resolution: resolution.name,
      range: range.key
    });
    let url = window.location.pathname + "?" + query.toString();
    if (url !== window.location.pathname + window.location.search) {
      // replace rather than push: picking a value is not a navigation step to go back to
      window.history.replaceState({}, "", url);
    }
  }

  async function load(game, itemType, descriptor, provider, resolution, range) {
    if (game == null || itemType == null || descriptor == null || provider == null) {
      return;
    }
    let to = new Date();
    let from = new Date(to.getTime() - range.hours * 60 * 60 * 1000);
    loading = true;
    error = null;
    try {
      segments = await backend.avgPerTopHistory(game.name, itemType.name, descriptor.name, provider, resolution.seconds, from, to);
    } catch (e) {
      error = e.message;
      segments = [];
    } finally {
      loading = false;
    }
  }

  function trace(xs, field, name, axis) {
    return {
      mode: 'lines',
      name: name,
      x: xs,
      y: segments.map(s => s[field]),
      yaxis: axis
    };
  }

  function draw(div, title, traces) {
    if (div == null) {
      return;
    }
    plotly.newPlot(div, traces, Object.assign({}, LAYOUT, {title: title}), {responsive: true});
  }

  $: {
    let xs = segments.map(s => new Date(s.segment * 1000));
    draw(pricePlotDiv, 'Средняя цена по топ N офферов', [
      trace(xs, 'avgPrice_3', 'avg_price_3'),
      trace(xs, 'avgPrice_5', 'avg_price_5'),
      trace(xs, 'avgPrice_7', 'avg_price_7'),
      trace(xs, 'avgPrice_10', 'avg_price_10'),
      trace(xs, 'offers', 'offers', 'y2')
    ]);
    draw(quantityPlotDiv, 'Среднее количество по топ N офферов', [
      trace(xs, 'quantity_3', 'quantity_3'),
      trace(xs, 'quantity_5', 'quantity_5'),
      trace(xs, 'quantity_7', 'quantity_7'),
      trace(xs, 'quantity_10', 'quantity_10'),
      trace(xs, 'offers', 'offers', 'y2')
    ]);
  }
</script>

<main>
  <div class="row g-2 mb-3">
    <div class="col">
      <select class="form-select" aria-label="Выбор игры" bind:value={game}>
        {#each games as g}
          <option value={g}>{g.name}</option>
        {/each}
      </select>
    </div>
    <div class="col">
      <select class="form-select" aria-label="Выбор валюты" bind:value={itemType} disabled={game == null}>
        {#each (game == null ? [] : game.subtypes) as s}
          <option value={s}>{s.name}</option>
        {/each}
      </select>
    </div>
    <div class="col">
      <select class="form-select" aria-label="Выбор дескриптора" bind:value={descriptor} disabled={itemType == null}>
        {#each (itemType == null ? [] : itemType.descriptors) as d}
          <option value={d}>{d.name}</option>
        {/each}
      </select>
    </div>
    <div class="col">
      <select class="form-select" aria-label="Выбор площадки" bind:value={provider}>
        {#each providers as pr}
          <option value={pr}>{pr}</option>
        {/each}
      </select>
    </div>
    <div class="col">
      <select class="form-select" aria-label="Выбор разрешения" bind:value={resolution}>
        {#each RESOLUTIONS as r}
          <option value={r}>{r.name}</option>
        {/each}
      </select>
    </div>
    <div class="col">
      <select class="form-select" aria-label="Выбор периода" bind:value={range}>
        {#each RANGES as r}
          <option value={r}>{r.name}</option>
        {/each}
      </select>
    </div>
  </div>

  {#if error != null}
    <div class="alert alert-danger">Не удалось загрузить историю: {error}</div>
  {:else if loading}
    <p>Загрузка…</p>
  {:else if descriptor != null && segments.length === 0}
    <p>Нет данных за выбранный период.</p>
  {/if}

  <div bind:this={pricePlotDiv}></div>
  <div bind:this={quantityPlotDiv}></div>
</main>
